/**
 * מסך התחברות הפאנל — TASKS 3.1, 6.3ב · DESIGN.md 8.5.
 *
 * ⚠️ תוויות אמיתיות (`<label for>`), `aria-invalid` + `aria-describedby` על
 *    שדה שגוי, ושגיאה כללית ב-`role="alert"` — דפוסי החובה של PRD 12.4.
 *
 * ⭐ DESIGN.md 8.5 — "כרטיס ממורכז max-w-sm על רקע surface, לוגו מעל...
 *    הודעת נעילת חשבון בבאנר danger עם אייקון." `ACCOUNT_LOCKED` (קוד השגיאה
 *    מהשרת, ראו `routes/admin/auth.ts`) מקבל את הבאנר המודגש; כל שגיאה אחרת
 *    (למשל סיסמה שגויה) נשארת הודעת טופס רגילה - היא לא אירוע חמור כמו נעילה.
 */
import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockSimple } from '@phosphor-icons/react';
import { Button } from '../components/Button.js';
import { COMPANY_NAME, LOGO_HEIGHT, LOGO_WIDTH } from '../content/businessInfo.js';
import { adminLogin, isAdminApiError } from './adminApi.js';
import { FieldError } from '../components/FieldError.js';

export function LoginPage() {
  const navigate = useNavigate();
  const usernameId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsLockedOut(false);
    setSubmitting(true);

    try {
      await adminLogin(username, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (isAdminApiError(err)) {
        setError(err.message);
        setIsLockedOut(err.code === 'ACCOUNT_LOCKED');
      } else {
        setError('אירעה שגיאה. נסו שוב.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="bg-surface flex min-h-screen items-center justify-center px-6 py-16">
      <div
        className="bg-canvas w-full max-w-sm rounded-lg border border-line p-6"
        style={{ boxShadow: 'var(--shadow-card)' }}
      >
        <img
          src="/brand/logo.png"
          alt={COMPANY_NAME}
          width={LOGO_WIDTH}
          height={LOGO_HEIGHT}
          className="mx-auto h-14 w-auto"
        />

        <h1 className="mt-4 text-center text-2xl font-bold">כניסה לפאנל הניהול</h1>
        <p className="text-muted mt-1 text-center text-sm">{COMPANY_NAME}</p>

        {isLockedOut && error && (
          <div className="banner-danger mt-6 flex items-start gap-2 rounded-md p-3 text-sm">
            <LockSimple size={18} weight="regular" aria-hidden="true" className="mt-0.5 shrink-0" />
            <p id={errorId} role="alert">
              {error}
            </p>
          </div>
        )}

        <form className="mt-6 space-y-5" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div>
            <label htmlFor={usernameId} className="block text-sm font-semibold">
              דוא&quot;ל
            </label>
            <input
              id={usernameId}
              name="username"
              type="email"
              autoComplete="username"
              required
              aria-required="true"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="ltr-inline mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
            />
          </div>

          <div>
            <label htmlFor={passwordId} className="block text-sm font-semibold">
              סיסמה
            </label>
            <input
              id={passwordId}
              name="password"
              type="password"
              autoComplete="current-password"
              required
              aria-required="true"
              aria-invalid={error && !isLockedOut ? 'true' : undefined}
              aria-describedby={error && !isLockedOut ? errorId : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
            />
          </div>

          {error && !isLockedOut && <FieldError id={errorId}>{error}</FieldError>}

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'מתחבר…' : 'התחברות'}
          </Button>
        </form>
      </div>
    </main>
  );
}
